import { type AvatarRootProps, Avatar as BaseAvatar } from "@ark-ui/react";
import { User } from "@phosphor-icons/react";
import clsx from "clsx";
import * as React from "react";

const getInitials = (name = "") =>
  name
    .split(" ")
    .map((part) => part[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

interface AvatarProps extends AvatarRootProps {
  src?: string;
  name?: string;
  fallbackIcon?: React.ReactNode;
  size?: "xs" | "sm" | "md" | "lg";
  withRing?: boolean;
  className?: string;
  classNames?: {
    root?: string;
    image?: string;
    fallback?: string;
  };
  variant?: "outline";
}

const Avatar = React.forwardRef<
  React.ElementRef<typeof BaseAvatar.Image>,
  AvatarProps
>((props, ref) => {
  const {
    classNames,
    className,
    src,
    size = "sm",
    withRing = false,
    name,
    variant,
    fallbackIcon = <User size={24} fill="currentColor" />,
    ...rest
  } = props;
  return (
    <BaseAvatar.Root
      className={clsx("ox-reset ox-AvatarRoot", className, classNames?.root)}
      {...rest}
      data-size={size}
      data-ring={withRing}
      data-variant={variant}
    >
      <BaseAvatar.Fallback
        data-size={size}
        data-variant={variant}
        className={clsx("ox-reset ox-AvatarFallback", classNames?.fallback)}
      >
        {name ? getInitials(name) : fallbackIcon}
      </BaseAvatar.Fallback>
      {src ? (
        <BaseAvatar.Image
          data-ring={withRing}
          className={clsx("ox-reset ox-AvatarImage", classNames?.image)}
          src={src}
          alt={name}
          ref={ref}
        />
      ) : null}
    </BaseAvatar.Root>
  );
});
Avatar.displayName = "Avatar";
export { Avatar };
export type { AvatarProps };
